// src/pages/JvStatus.js
// Public JV-partner view. No HomeIQa login required — partner opens the
// shareable URL (/jv-status/:tokenId), enters the password Laketia gave
// them, and sees a read-only outreach report: address, owner, contacted
// yes/no + date, expected next contact date.
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const BURG = '#6B1E2E';
const BURG_DK = '#4A1220';
const GOLD = '#C9A84C';
const GOLD_LITE = '#E2C47A';
const CREAM = '#FAF7F2';
const BORDER = '#E5E7EB';

export default function JvStatus() {
  const { tokenId } = useParams();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showDead, setShowDead] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 700);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Cache the working password so the poll loop below can refetch without
  // asking again. Kept in memory only — never persisted.
  const authedPasswordRef = useRef(null);

  const fetchStatus = async (pw) => {
    const fn = httpsCallable(functions, 'getJvStatus');
    const res = await fn({ tokenId, password: pw });
    return res.data;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!password.trim()) return;
    setLoading(true); setError('');
    try {
      const res = await fetchStatus(password.trim());
      authedPasswordRef.current = password.trim();
      setData(res);
    } catch (err) {
      setError(err?.message || 'Failed to load. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // Live refresh — every 60 seconds while the page is visible, re-pull
  // the latest snapshot so the JV partner always sees current status.
  useEffect(() => {
    if (!data || !authedPasswordRef.current) return;
    let cancelled = false;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      setRefreshing(true);
      try {
        const res = await fetchStatus(authedPasswordRef.current);
        if (!cancelled) setData(res);
      } catch { /* silent — next tick will retry */ }
      finally { if (!cancelled) setRefreshing(false); }
    };
    const id = setInterval(tick, 60000);
    // Also fetch on tab-focus so switching back gives fresh data instantly.
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line
  }, [data && !!authedPasswordRef.current]);

  const refreshNow = async () => {
    if (!authedPasswordRef.current) return;
    setRefreshing(true);
    try { setData(await fetchStatus(authedPasswordRef.current)); }
    catch (e) { setError(e?.message || 'Refresh failed'); }
    finally { setRefreshing(false); }
  };

  const fmtDate = (iso, withTime = false) => {
    if (!iso) return '';
    const d = new Date(iso);
    return withTime
      ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const exportCsv = () => {
    if (!data?.leads?.length) return;
    const header = ['Address', 'Owner', 'Status', 'Notes', 'Contacted', 'Contacted On', 'Responded', 'Responded On', 'Expected Next Contact'];
    const csv = [header.join(',')].concat(
      data.leads.map(r => [
        r.address, r.ownerName, r.jvStatus || '', r.notes || '',
        r.contacted ? 'Yes' : 'No', r.contactedAt ? fmtDate(r.contactedAt, true) : '',
        r.responded ? 'Yes' : 'No', r.respondedAt ? fmtDate(r.respondedAt, true) : '',
        r.expectedContactAt ? fmtDate(r.expectedContactAt, true) : ''
      ].map(v => {
        const s = String(v == null ? '' : v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `outreach-status-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (data) {
    const contactedCount = data.leads.filter(l => l.contacted).length;
    const respondedCount = data.leads.filter(l => l.responded).length;
    // Bucket by JV status color, then filter, then split dead-vs-alive.
    const colorOrder = { green: 0, gold: 1, blue: 2, gray: 3, red: 4 };
    const filterMatch = (r) => {
      if (filter === 'all') return true;
      if (filter === 'interested') return r.jvStatusColor === 'green';
      if (filter === 'in-progress') return r.jvStatusColor === 'gold' || r.jvStatusColor === 'blue';
      if (filter === 'scheduled') return !r.contacted && !r.responded && r.expectedContactAt;
      if (filter === 'dead') return r.jvStatusColor === 'red';
      return true;
    };
    const sorted = [...data.leads].sort((a, b) => (colorOrder[a.jvStatusColor] ?? 9) - (colorOrder[b.jvStatusColor] ?? 9));
    const alive = sorted.filter(r => r.jvStatusColor !== 'red' && filterMatch(r));
    const dead  = sorted.filter(r => r.jvStatusColor === 'red' && filterMatch(r));
    const counts = {
      all: data.leads.length,
      interested: data.leads.filter(r => r.jvStatusColor === 'green').length,
      'in-progress': data.leads.filter(r => r.jvStatusColor === 'gold' || r.jvStatusColor === 'blue').length,
      scheduled: data.leads.filter(r => !r.contacted && !r.responded && r.expectedContactAt).length,
      dead: data.leads.filter(r => r.jvStatusColor === 'red').length
    };
    return (
      <div style={s.shell}>
        <div style={s.container}>
          <header style={s.head}>
            <div>
              <div style={s.eyebrow}>Outreach status · Tag: {data.tag}{data.partner ? ` · Shared with ${data.partner.name}` : ''}</div>
              <h1 style={s.h1}>JV outreach progress</h1>
              <div style={s.stats}>
                <span><strong>{data.leads.length}</strong> properties</span>
                <span><strong>{contactedCount}</strong> contacted</span>
                <span><strong>{respondedCount}</strong> responded</span>
              </div>
              <div style={s.gen}>
                Live — updates every minute. Last refreshed {fmtDate(data.generatedAt, true)}
                {refreshing && <span style={{ marginLeft: 8, color: GOLD }}>· refreshing…</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={refreshNow} disabled={refreshing} style={s.btnGhost}>↻ Refresh</button>
              <button type="button" onClick={exportCsv} style={s.btnGold}>Download CSV</button>
            </div>
          </header>

          {/* Filter chips */}
          <div style={s.filterRow}>
            {[
              { key: 'all', label: 'All' },
              { key: 'interested', label: 'Interested' },
              { key: 'in-progress', label: 'In progress' },
              { key: 'scheduled', label: 'Scheduled' },
              { key: 'dead', label: 'Dead' }
            ].map(f => (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)}
                style={{ ...s.chip, ...(filter === f.key ? s.chipActive : {}) }}>
                {f.label} <span style={{ opacity: 0.7 }}>{counts[f.key] || 0}</span>
              </button>
            ))}
          </div>

          {isMobile
            ? <StatusCards rows={alive} fmtDate={fmtDate} />
            : <StatusTable rows={alive} fmtDate={fmtDate} />}

          {dead.length > 0 && (
            <div style={s.deadWrap}>
              <button type="button" onClick={() => setShowDead(v => !v)} style={s.deadHeader}>
                <span style={{ ...statusPillStyle('red'), marginRight: 10 }}>Dead deals</span>
                <span style={{ color: '#6B7280', fontSize: 13, flex: 1, textAlign: 'left' }}>{dead.length} archived</span>
                <span style={s.deadChevron}>{showDead ? '▾' : '▸'}</span>
              </button>
              {showDead && (isMobile
                ? <StatusCards rows={dead} fmtDate={fmtDate} muted />
                : <StatusTable rows={dead} fmtDate={fmtDate} muted />)}
            </div>
          )}

          <footer style={s.footer}>
            Shared by <strong>T. Laketia Jones</strong> · read-only snapshot · reload to refresh.
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div style={s.shell}>
      <form onSubmit={submit} style={s.loginCard}>
        <div style={s.logoRow}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" style={{ width: 56, height: 56 }}>
            <circle cx="50" cy="50" r="47" fill={BURG_DK} />
            <circle cx="50" cy="50" r="47" fill="none" stroke={GOLD} strokeWidth="1.5" />
            <text x="50" y="63" fontFamily="Georgia, serif" fontSize="32" fontWeight="600" textAnchor="middle" letterSpacing="1" fill={GOLD}>T<tspan fill={GOLD_LITE}>L</tspan>J</text>
          </svg>
        </div>
        <h1 style={s.loginTitle}>JV outreach status</h1>
        <p style={s.loginLede}>Enter the password T. Laketia shared with you to view the current outreach status for this project.</p>
        <label style={s.label}>
          Password
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoFocus
            style={s.input} placeholder="Password from your text or email" />
        </label>
        {error && <div style={s.error}>{error}</div>}
        <button type="submit" disabled={loading || !password.trim()} style={{ ...s.btnPrimary, ...(loading || !password.trim() ? s.btnDisabled : {}) }}>
          {loading ? 'Loading…' : 'View status →'}
        </button>
      </form>
    </div>
  );
}

// Mobile card list — one card per lead. Reads cleanly on phones.
function StatusCards({ rows, fmtDate, muted = false }) {
  if (!rows.length) return <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12 }}>Nothing here.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, opacity: muted ? 0.85 : 1 }}>
      {rows.map(r => (
        <div key={r.id} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 14, boxShadow: '0 1px 2px rgba(17,24,39,0.04)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: 1.35, flex: 1 }}>{r.address}</div>
            <span style={statusPillStyle(r.jvStatusColor)}>{r.jvStatus}</span>
          </div>
          <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 8 }}>{r.ownerName}</div>
          {r.notes && <div style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.5, marginBottom: 8 }}>{r.notes}</div>}
          <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#6B7280', flexWrap: 'wrap' }}>
            <span>Contacted: <strong style={{ color: '#374151' }}>{r.contactedAt ? fmtDate(r.contactedAt) : (r.contacted ? 'Yes' : 'No')}</strong></span>
            {r.expectedContactAt && <span>Next: <strong style={{ color: '#374151' }}>{fmtDate(r.expectedContactAt)}</strong></span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// Reusable table so both the alive + dead sections render the same shape.
function StatusTable({ rows, fmtDate, muted = false }) {
  if (!rows.length) return <div style={{ padding: 30, textAlign: 'center', color: '#9CA3AF', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12 }}>Nothing here.</div>;
  return (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(17,24,39,0.05)', opacity: muted ? 0.85 : 1 }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={jvT.th}>Address</th>
              <th style={jvT.th}>Owner</th>
              <th style={jvT.th}>Status</th>
              <th style={jvT.th}>Notes</th>
              <th style={jvT.th}>Contacted</th>
              <th style={jvT.th}>Expected next</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td style={jvT.td}>{r.address}</td>
                <td style={jvT.td}>{r.ownerName}</td>
                <td style={jvT.td}>
                  <span style={statusPillStyle(r.jvStatusColor)}>{r.jvStatus || '—'}</span>
                </td>
                <td style={jvT.tdNote}>{r.notes || <span style={{ color: '#9CA3AF' }}>—</span>}</td>
                <td style={jvT.td}>{r.contactedAt ? fmtDate(r.contactedAt) : (r.contacted ? 'Yes' : 'No')}</td>
                <td style={jvT.td}>{r.expectedContactAt ? fmtDate(r.expectedContactAt) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const jvT = {
  th: { padding: '14px 16px', textAlign: 'left', background: '#FAFAFA', borderBottom: '1px solid #E5E7EB', fontSize: 11, fontWeight: 600, color: '#6B7280' },
  td: { padding: '14px 16px', borderBottom: '1px solid #E5E7EB', color: '#374151' },
  tdNote: { padding: '14px 16px', borderBottom: '1px solid #E5E7EB', color: '#4B5563', fontSize: 12, lineHeight: 1.5, maxWidth: 320 }
};

function statusPillStyle(color) {
  const map = {
    green:  { background: '#DCFCE7', color: '#166534' },
    red:    { background: '#FEE2E2', color: '#991B1B' },
    gold:   { background: '#FEF3C7', color: '#78350F' },
    blue:   { background: '#DBEAFE', color: '#1E40AF' },
    gray:   { background: '#F3F4F6', color: '#6B7280' }
  };
  const c = map[color] || map.gray;
  return { display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', ...c };
}

const s = {
  shell: { minHeight: '100vh', background: CREAM, padding: '20px 14px', fontFamily: "'Inter', system-ui, sans-serif", color: '#111827' },
  container: { maxWidth: 1080, margin: '0 auto' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 20 },
  eyebrow: { fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: GOLD, marginBottom: 6 },
  h1: { fontFamily: "'Manrope', sans-serif", fontSize: 24, fontWeight: 800, color: BURG_DK, margin: '0 0 10px', letterSpacing: '-0.02em' },
  stats: { display: 'flex', gap: 20, fontSize: 14, color: '#374151', marginBottom: 6 },
  gen: { fontSize: 12, color: '#9CA3AF' },
  tableWrap: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(17,24,39,0.05)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '14px 16px', textAlign: 'left', background: '#FAFAFA', borderBottom: `1px solid ${BORDER}`, fontSize: 11, fontWeight: 600, color: '#6B7280', letterSpacing: '0.02em' },
  tr: {},
  td: { padding: '14px 16px', borderBottom: `1px solid ${BORDER}`, color: '#374151' },
  pillGreen: { display: 'inline-block', background: '#DCFCE7', color: '#166534', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 },
  pillGray: { display: 'inline-block', background: '#F3F4F6', color: '#6B7280', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 },
  tdNote: { padding: '14px 16px', borderBottom: `1px solid ${BORDER}`, color: '#4B5563', fontSize: 12, lineHeight: 1.5, maxWidth: 320 },
  filterRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  chip: { padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'Inter', sans-serif", border: `1px solid ${BORDER}`, background: '#fff', color: BURG_DK },
  chipActive: { background: BURG, color: '#fff', border: `1px solid ${BURG}` },
  deadWrap: { marginTop: 24, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, overflow: 'hidden' },
  deadHeader: { width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', background: '#FAFAFA', border: 'none', cursor: 'pointer', fontFamily: "'Inter', sans-serif", textAlign: 'left' },
  deadChevron: { marginLeft: 'auto', fontSize: 16, color: '#6B7280' },
  footer: { marginTop: 20, fontSize: 12, color: '#9CA3AF', textAlign: 'center' },
  btnGold: { background: GOLD, color: BURG_DK, border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: '#fff', color: BURG_DK, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 500, cursor: 'pointer' },

  loginCard: { maxWidth: 420, margin: '80px auto', background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 16, padding: '40px 32px 32px', textAlign: 'center', boxShadow: '0 4px 24px rgba(107,30,46,0.08)' },
  logoRow: { display: 'flex', justifyContent: 'center', marginBottom: 20 },
  loginTitle: { fontFamily: "'Manrope', sans-serif", fontSize: 22, fontWeight: 800, color: BURG_DK, margin: '0 0 10px', letterSpacing: '-0.01em' },
  loginLede: { fontSize: 14, color: '#4B5563', lineHeight: 1.6, margin: '0 0 24px' },
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 600, color: '#374151', textAlign: 'left', marginBottom: 12 },
  input: { padding: '12px 14px', border: `1px solid ${BORDER}`, borderRadius: 10, fontSize: 15, fontFamily: 'inherit', outline: 'none' },
  error: { background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 },
  btnPrimary: { width: '100%', background: BURG, color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' }
};
